const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class MerowenAboutSection extends HTMLElement {
  #observer = null;
  #motionQuery = null;
  #onMotionChange = null;
  #onBlockSelect = null;
  #onSectionSelect = null;
  #onSectionReorder = null;

  connectedCallback() {
    this.#motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    this.#onMotionChange = () => this.#syncMotionPreference();
    this.#onBlockSelect = (event) => this.#handleBlockSelect(event);
    this.#onSectionSelect = (event) => this.#handleSectionSelect(event);
    this.#onSectionReorder = () => this.#refreshRevealOrder();

    this.#motionQuery.addEventListener('change', this.#onMotionChange);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:section:select', this.#onSectionSelect);
    document.addEventListener('shopify:section:reorder', this.#onSectionReorder);

    this.#refreshRevealOrder();
    this.#syncMotionPreference();
  }

  disconnectedCallback() {
    this.#disconnectObserver();
    this.#motionQuery?.removeEventListener('change', this.#onMotionChange);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:reorder', this.#onSectionReorder);
  }

  #syncMotionPreference() {
    const revealEnabled = this.dataset.revealEnabled === 'true';
    const reducedMotion = this.#motionQuery?.matches ?? false;
    const designMode = document.documentElement.classList.contains('shopify-design-mode');

    this.#disconnectObserver();

    if (!revealEnabled || reducedMotion || designMode || !('IntersectionObserver' in window)) {
      this.#revealAll();
      return;
    }

    this.classList.add('is-reveal-ready');
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-revealed');
          this.#observer?.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.01 }
    );

    for (const element of this.querySelectorAll('[data-reveal]:not(.is-revealed)')) {
      this.#observer.observe(element);
    }
  }

  #refreshRevealOrder() {
    const elements = [...this.querySelectorAll('[data-reveal]')];
    elements.forEach((element, index) => {
      const staggerIndex = Number(element.dataset.revealIndex ?? index);
      element.style.setProperty('--ma-reveal-delay', `${Math.min(staggerIndex * 55, 300)}ms`);
    });
  }

  #revealAll() {
    this.classList.remove('is-reveal-ready');
    for (const element of this.querySelectorAll('[data-reveal]')) {
      element.classList.add('is-revealed');
    }
  }

  #handleBlockSelect(event) {
    const block = event.target instanceof Element ? event.target : null;
    if (!block || !this.contains(block)) return;
    block.classList.add('is-revealed');
    block.scrollIntoView({ block: 'center', behavior: 'auto' });
  }

  #handleSectionSelect(event) {
    const selected = event.target instanceof Element ? event.target : null;
    if (!selected || !selected.contains(this)) return;
    this.#revealAll();
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('merowen-about-section')) {
  customElements.define('merowen-about-section', MerowenAboutSection);
}
